"use client";

import { useEffect, useRef, useState } from "react";
import { CardTitle, CardDescription } from "@/components/ui/card";
// import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import "remixicon/fonts/remixicon.css";
import { Badge } from "@/components/ui/badge";
import {
  useQuadMediaApiToken,
  type TokenWithMonitoring,
} from "@/features/auth/hooks/useApiToken";
import { useSourceBasedStore } from "../../store/sourceStore";
import { getSourceIconsByMetadata } from "../../utils/getSourceIcons";
import { useTokenValidation } from "@/hooks/useTokenValidation";

interface SelectedSourcesProps {
  onEditSources?: () => void;
  onLaunch?: () => void;
  selectedToken?: TokenWithMonitoring | null;
}

export default function SelectedSources({
  onEditSources,
  onLaunch,
  selectedToken,
}: SelectedSourcesProps) {
  const { apiToken } = useQuadMediaApiToken();
  const { selectedPublications, search, setSearch, togglePublication } =
    useSourceBasedStore();
  const selected = Array.from(selectedPublications);

  const { isLaunchDisabled, effectiveToken } = useTokenValidation({
    apiToken,
    selectedToken,
    showTokenDropdown: true,
    additionalValidation: () => selected.length > 0,
  });

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  const handleToggleSort = () =>
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));

  const sortedSources = [...selected].sort((a, b) =>
    sortOrder === "asc"
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setSearch]);

  const categories = Array.from(new Set(selected.map((p) => p.category)));

  const toggleTag = (tag: string) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const filteredByTagAndSearch = sortedSources.filter((p) => {
    const matchesTag =
      activeTags.length === 0 || activeTags.includes(p.category);
    const matchesSearch = search
      ? p.name.toLowerCase().includes(search.toLowerCase())
      : true;
    return matchesTag && matchesSearch;
  });

  return (
    <div className="flex h-full flex-col">
      <CardTitle className="mb-5 text-[28px] leading-[36px] -tracking-wide text-neutral-950">
        Source Monitoring
      </CardTitle>
      <div className="mb-5 flex w-full flex-row justify-between">
        <div className="flex w-full flex-row items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-row items-center gap-3">
              <CardTitle className="leading-normal">Selected Sources</CardTitle>
              <Badge variant="source">{selected.length}</Badge>
            </div>
            <CardDescription>Select media sources to monitor</CardDescription>
          </div>
        </div>
        {/* <div
          ref={searchRef}
          className="relative max-h-[45px] w-auto min-w-[200px]"
        >
          <Input
            variant="search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div> */}
      </div>

      <div className="flex flex-row flex-wrap items-center gap-3 rounded-t-[12px] border border-b border-neutral-200 bg-neutral-100 p-3">
        <p className="text-[16px] leading-normal font-bold text-neutral-500">
          Show:
        </p>
        {categories.map((tag) => {
          const isActive = activeTags.includes(tag);

          const categoryIconMap: Record<string, React.ReactNode> = {
            print: <i className="ri-newspaper-fill" />,
            online: <i className="ri-global-fill" />,
            radio: <i className="ri-radio-fill" />,
            tv: <i className="ri-tv-fill" />,
          };

          const icon = categoryIconMap[tag] ?? <i className="ri-folder-line" />;

          return (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`flex cursor-pointer items-center text-[16px] font-medium transition-colors`}
            >
              <span
                className={`${
                  isActive ? "text-brand-500" : "text-neutral-500"
                } flex items-center`}
              >
                {icon}
              </span>
            </button>
          );
        })}

        <button onClick={handleToggleSort} className="ml-auto">
          {sortOrder === "desc" ? (
            <i className="ri-arrow-down-s-fill text-[18px]"></i>
          ) : (
            <i className="ri-arrow-up-s-fill text-[18px]"></i>
          )}
        </button>
      </div>

      <div className="flex h-[calc(100vh-315px)] flex-col overflow-hidden rounded-b-[12px] border border-neutral-200 bg-neutral-50">
        <div
          className="scrollbar-thin flex-1 overflow-y-auto px-2"
          style={{ scrollbarGutter: "stable" }}
        >
          {filteredByTagAndSearch.length === 0 && (
            <p className="p-4 text-sm text-neutral-500">
              No sources selected yet
            </p>
          )}
          {filteredByTagAndSearch.map((name) => (
            <div
              key={name.id}
              className="flex items-center justify-between gap-2 border-b border-neutral-200 bg-neutral-50 py-2 pl-3 text-sm font-medium tracking-normal text-neutral-950 transition-colors"
            >
              <span className="min-w-0 flex-1 truncate">{name.name}</span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {getSourceIconsByMetadata(
                    name.category,
                    name.subcategory,
                  ).map((icon, i) => (
                    <span key={i} className="cursor-pointer">
                      {icon}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => togglePublication(name)}
                  className="flex-shrink-0 p-1 text-neutral-400 transition-colors hover:text-red-500"
                  title="Remove source"
                >
                  <i className="ri-close-line text-[12px]"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex w-full flex-row justify-start gap-3">
        <Button
          variant="outline"
          onClick={onEditSources}
          className="h-[48px] !max-w-[120px] hover:scale-none"
        >
          Back
        </Button>
        <Button
          onClick={onLaunch}
          className="h-[48px] !max-w-[240px] hover:scale-none"
          disabled={isLaunchDisabled}
          icon
        >
          {effectiveToken ? (
            <>
              <i className="ri-arrow-right-s-fill text-[18px]"></i>
              Relaunch
            </>
          ) : (
            "Missing API Token"
          )}
        </Button>
      </div>
    </div>
  );
}
